/**
 * Cost Summary Cards Component
 *
 * Displays key cost metrics: Total Cost, Daily Average, and Projected Monthly.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';
import { formatCurrency } from '@/lib/formatters';
import type { CostsSummary } from '@/types/dashboard';

interface CostSummaryCardsProps {
  data: CostsSummary | undefined;
  isLoading: boolean;
  timeRange: string;
}

interface CostCardProps {
  title: string;
  value: number | undefined;
  isLoading: boolean;
  tooltip: string;
}

function CostCard({ title, value, isLoading, tooltip }: CostCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <div className="text-2xl font-bold">{formatCurrency(value)}</div>
        )}
      </CardContent>
    </Card>
  );
}

export function CostSummaryCards({
  data,
  isLoading,
  timeRange,
}: CostSummaryCardsProps) {
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <CostCard
        title="Total Cost"
        value={data?.totalCost}
        isLoading={isLoading}
        tooltip={`Total AWS costs for Compass-tagged resources over the ${timeRange} period`}
      />
      <CostCard
        title="Daily Average"
        value={data?.dailyAverage}
        isLoading={isLoading}
        tooltip="Average daily cost based on the selected time period"
      />
      <CostCard
        title="Projected Monthly"
        value={data?.projectedMonthly}
        isLoading={isLoading}
        tooltip="Projected 30-day cost based on the current daily average"
      />
    </div>
  );
}
